/**
 * WhatsNewBadge + WhatsNewInline, header changelog button + dropdown.
 * Extracted from Header.js (Phase 3 refactor, 2026-05-03).
 */
import React, { useState, useEffect, useRef } from "react";
import { Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const WhatsNewInline = ({ onClose }) => {
  const [entries, setEntries] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/changelog/entries?limit=10`);
        const data = await res.json();
        setEntries(data.entries || []);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const catColors = { feature: "text-emerald-400", improvement: "text-blue-400", fix: "text-amber-400" };
  const catLabels = { feature: "New", improvement: "Improved", fix: "Fixed" };

  return (
    <div
      ref={ref}
      className="
        fixed left-2 right-2 top-[64px] z-50 overflow-hidden
        sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80
        max-h-[80vh] overflow-y-auto
        rounded-2xl border-2 border-amber-500/30
        bg-slate-900 sm:bg-slate-900/98 backdrop-blur-md
        shadow-2xl shadow-black/60
      "
      style={{ backgroundColor: "rgba(15, 23, 42, 0.99)" }}
      data-testid="whats-new-dropdown"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
        <h3 className="text-white text-sm font-fredoka font-bold flex items-center gap-2">
          <Sparkles size={14} className="text-amber-400" /> What's New
        </h3>
        <button onClick={onClose} className="text-white/30 hover:text-white/60 text-xs font-bold">
          Close
        </button>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="py-8 text-center">
            <Sparkles size={24} className="mx-auto text-white/15 mb-2" />
            <p className="text-white/30 text-xs">No updates yet</p>
          </div>
        ) : (
          entries.map((e, i) => (
            <div key={e.id || i} className="px-4 py-3 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold ${catColors[e.category] || "text-white/40"}`}>
                  {catLabels[e.category] || e.category}
                </span>
                {e.version && <span className="text-white/15 text-[9px]">v{e.version}</span>}
              </div>
              <p className="text-white text-xs font-bold">{e.title}</p>
              <p className="text-white/35 text-[11px] mt-0.5 leading-relaxed">{e.description}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export const WhatsNewBadge = ({ token }) => {
  const [count, setCount] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!token || token === "demo-token") return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/changelog/unseen`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setCount(data.count || 0);
      } catch { /* silent */ }
    })();
  }, [token]);

  const handleDismiss = async () => {
    setShowModal(false);
    setCount(0);
    try {
      await fetch(`${API}/api/changelog/mark-seen`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* silent */ }
  };

  if (count === 0 && !showModal) return null;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="relative p-1.5 rounded-lg text-white/50 hover:text-amber-400 transition-colors"
        data-testid="whats-new-badge"
      >
        <Sparkles size={18} />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center"
            style={{ boxShadow: "0 0 8px rgba(245,158,11,0.5)" }}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {showModal && <WhatsNewInline onClose={handleDismiss} />}
    </>
  );
};
